import { CUSTOM_MODEL_OPTION_VALUE } from '../constants';

export type ModelPickerOption = {
  value: string;
  label: string;
  desc: string;
};

type ModelPickerDeps = {
  button: HTMLElement | null;
  labelEl: HTMLElement | null;
  sessionState: any;
  selectedAgentProvider: () => string;
  showOptionPicker: (title: string, options: ModelPickerOption[], currentValue: string, onSelect: (value: string) => void) => void;
  send: (payload: any) => void;
};

export type ModelPickerController = {
  open: () => void;
  setLabel: (model?: string) => void;
};

function normalizeAgentValue(agent: unknown): string {
  return String(agent || '').trim().toLowerCase() || 'claude';
}

function normalizeModelValue(model: unknown): string {
  return String(model || '').trim();
}

export function getSessionModelOptions(agent: string, sessionState: any): ModelPickerOption[] {
  const normalizedAgent = normalizeAgentValue(agent);
  const seen = new Set<string>();
  const options: ModelPickerOption[] = [];

  function addOption(value: string, label: string, desc: string) {
    const normalizedValue = normalizeModelValue(value);
    const effectiveValue = normalizedValue || 'default';
    if (seen.has(effectiveValue)) return;
    seen.add(effectiveValue);
    options.push({
      value: effectiveValue,
      label,
      desc,
    });
  }

  addOption('default', 'default', '使用当前会话的默认模型配置');

  const currentModel = normalizeModelValue(sessionState?.currentModel);
  if (currentModel) {
    addOption(currentModel, currentModel, '当前会话模型');
  }

  const sessions = Array.isArray(sessionState?.sessions) ? sessionState.sessions : [];
  for (const session of sessions) {
    if (normalizeAgentValue(session?.agent) !== normalizedAgent) continue;
    const model = normalizeModelValue(session?.model);
    if (!model) continue;
    addOption(model, model, session?.id === sessionState?.currentSessionId ? '当前会话已保存模型' : '历史会话模型');
  }

  options.push({
    value: CUSTOM_MODEL_OPTION_VALUE,
    label: '自定义模型…',
    desc: '手动输入一个新的模型 ID',
  });
  return options;
}

export function bindModelPickerButton(deps: ModelPickerDeps): ModelPickerController {
  function setButtonDisabled(disabled: boolean): void {
    if (!deps.button) return;
    deps.button.toggleAttribute('disabled', disabled);
    deps.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function setLabel(model = ''): void {
    const hasSession = !!deps.sessionState?.currentSessionId;
    if (!deps.labelEl) return;
    if (!hasSession) {
      deps.labelEl.textContent = '模型: 先进入会话';
      setButtonDisabled(true);
      return;
    }
    const normalizedModel = normalizeModelValue(model || deps.sessionState?.currentModel);
    deps.labelEl.textContent = `模型: ${normalizedModel || 'default'}`;
    setButtonDisabled(false);
  }

  function commitSelection(value: string): void {
    if (!deps.sessionState?.currentSessionId) return;
    deps.send({
      type: 'set_session_model',
      sessionId: deps.sessionState.currentSessionId,
      agent: deps.sessionState.currentAgent || deps.selectedAgentProvider(),
      model: value,
    });
  }

  function open(): void {
    if (!deps.sessionState?.currentSessionId) {
      setLabel('');
      return;
    }
    const agent = deps.sessionState.currentAgent || deps.selectedAgentProvider();
    const options = getSessionModelOptions(agent, deps.sessionState);
    const currentValue = normalizeModelValue(deps.sessionState.currentModel) || 'default';
    deps.showOptionPicker('选择会话模型', options, currentValue, (value) => {
      if (value === CUSTOM_MODEL_OPTION_VALUE) {
        const customValue = window.prompt('输入模型 ID', normalizeModelValue(deps.sessionState.currentModel));
        const normalizedCustomValue = normalizeModelValue(customValue);
        if (!normalizedCustomValue) return;
        commitSelection(normalizedCustomValue);
        return;
      }
      commitSelection(value);
    });
  }

  setLabel('');

  return {
    open,
    setLabel,
  };
}
